import type { RouteDefinition, RouteParams } from './types';

interface Branch<TContainer, TLoaded> {
    readonly definitions: readonly RouteDefinition<TContainer, TLoaded>[];
    readonly pattern: string;
    readonly patterns: readonly string[];
    readonly score: number;
}

export interface MatchedBranch<TContainer, TLoaded> {
    readonly definitions: readonly RouteDefinition<TContainer, TLoaded>[];
    readonly params: RouteParams;
    readonly pathnameBases: readonly string[];
}

const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const joinPaths = (parent: string, child: string | undefined, index: boolean | undefined): string => {
    if (index || child === undefined || child === '') return parent || '/';
    if (child.startsWith('/')) return child;
    if (!parent || parent === '/') return `/${child}`;
    return `${parent.replace(/\/+$/, '')}/${child}`;
};

const scorePattern = (pattern: string, depth: number, index: boolean | undefined): number => {
    let score = depth + (index ? 2 : 0);
    for (const segment of pattern.split('/').filter(Boolean)) {
        if (segment === '*') score -= 1_000;
        else if (segment.startsWith(':') && segment.endsWith('?')) score += 10;
        else if (segment.startsWith(':')) score += 20;
        else score += 100;
    }
    return score;
};

function flattenRoutes<TContainer, TLoaded>(
    definitions: readonly RouteDefinition<TContainer, TLoaded>[],
    parentPattern = '',
    parents: readonly RouteDefinition<TContainer, TLoaded>[] = [],
    parentPatterns: readonly string[] = [],
): Branch<TContainer, TLoaded>[] {
    return definitions.flatMap((definition) => {
        const pattern = joinPaths(parentPattern, definition.path, definition.index);
        const chain = [...parents, definition];
        const patterns = [...parentPatterns, pattern];
        const ownBranch: Branch<TContainer, TLoaded> = {
            definitions: chain,
            pattern,
            patterns,
            score: scorePattern(pattern, chain.length, definition.index),
        };
        const childBranches = flattenRoutes(definition.children ?? [], pattern, chain, patterns);

        return [ownBranch, ...childBranches];
    });
}

const materializePattern = (pattern: string, params: RouteParams): string => {
    const segments = pattern.split('/').filter(Boolean);
    const pathname = segments
        .flatMap((segment) => {
            if (segment === '*') {
                return params['*']?.split('/').map(encodeURIComponent) ?? [];
            }
            if (!segment.startsWith(':')) return [segment];

            const optional = segment.endsWith('?');
            const name = segment.slice(1, optional ? -1 : undefined);
            const value = params[name];
            return value === undefined ? [] : [encodeURIComponent(value)];
        })
        .join('/');

    return `/${pathname}`;
};

function matchPattern(pattern: string, pathname: string): RouteParams | null {
    const names: string[] = [];
    let source = '^';
    const segments = pattern.split('/').filter(Boolean);

    if (segments.length === 0) {
        source += '/?';
    } else {
        for (const segment of segments) {
            if (segment === '*') {
                names.push('*');
                source += '(?:/(.*))?';
            } else if (segment.startsWith(':')) {
                const optional = segment.endsWith('?');
                const name = segment.slice(1, optional ? -1 : undefined);
                names.push(name);
                source += optional ? '(?:/([^/]+))?' : '/([^/]+)';
            } else {
                source += `/${escapeRegex(segment)}`;
            }
        }
        source += '/?';
    }
    source += '$';

    const result = new RegExp(source).exec(pathname);
    if (!result) return null;

    return Object.fromEntries(
        names.map((name, index) => {
            const value = result[index + 1];
            if (value === undefined) return [name, undefined];
            try {
                return [name, decodeURIComponent(value)];
            } catch {
                return [name, value];
            }
        }),
    );
}

export function createRouteMatcher<TContainer, TLoaded>(
    definitions: readonly RouteDefinition<TContainer, TLoaded>[],
): (pathname: string) => MatchedBranch<TContainer, TLoaded> {
    const branches = flattenRoutes(definitions).sort(
        (left, right) => right.score - left.score || right.definitions.length - left.definitions.length,
    );

    return (pathname) => {
        for (const branch of branches) {
            const params = matchPattern(branch.pattern, pathname);
            if (params) {
                return {
                    definitions: branch.definitions,
                    params,
                    pathnameBases: branch.patterns.map((pattern) => materializePattern(pattern, params)),
                };
            }
        }
        throw new Error(`No route matched pathname "${pathname}"`);
    };
}
